import React from 'react';
import { C } from '../constants';
import { SERVICE_CATALOG, servicioPorSku, serviciosParaAudiencia, audienciaDeRolPlataforma, serviciosPorCapa } from '../services/catalog';
import { currentClaims, audience, puedePremium, profileLabel, tieneMonitoreoFluxAI, tieneServicio, isTecnico } from '../services/auth';
import { tecnicoGanoEstudioGratis } from '../services/acceso';
import { fluxaiServiceUrl } from '../services/fluxai';
import { hubGet } from '../services/hub';
import { ESTADOS, setEntitlement, removeEntitlement, allAssignments, syncAllEntitlements } from '../services/entitlements';
import { listarUsuarios, syncUsuarios, ROL_LABELS } from '../services/usuarios';
import ServiceIcon from './ServiceIcon';

// Marca FluxAI (identidad de la OTRA app de la suite): cuando se CITA FluxAI se usan SUS
// colores (azul #0044A4 / verde #00C781) y SU logo ORIGINAL (el rayo), no el naranja de Solar Hub.
const FLUX = '#0044A4';
function FluxBolt({ size = 17 }) {
  return (
    <svg viewBox="0 0 91 91" width={size} height={size} fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M64 14L32.7647 40.7966H52.8824L28 76L57.1176 47.6271H37.5294L64 14Z" stroke="url(#svcFluxGrad)" strokeWidth="5" strokeLinejoin="round" />
      <defs><linearGradient id="svcFluxGrad" x1="46" y1="14" x2="46" y2="76" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00C781" /><stop offset="0.5" stopColor="#0044A4" /><stop offset="1" stopColor="#00C781" />
      </linearGradient></defs>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SERVICIOS — módulo de negocio de Solar Hub. Catálogo de servicios/planes (reflejo
// nativo del modelo de entitlements de FluxAI), role/entitlement-aware. Conserva la
// identidad de Solar Hub y comparte con la suite el rol técnico y los SKUs premium.
//   - Gratis → abierto.
//   - Premium → habilitado si puedePremium() (rol técnico/operador/admin o entitlement).
//   - ADMIN → ve TODO el catálogo y puede abrir cualquier servicio (sin gateo).
// El gateo duro se activa con el login real de api/ (hoy default-allow no rompe nada).
// ─────────────────────────────────────────────────────────────────────────────

// Panel ADMIN: asigna servicios a usuarios (por email) para PRUEBAS, mientras se
// habilita la compra. Mismo patrón que FluxAI (Usuario · Servicio · Estado · Asignar).
function EntitlementsAdmin({ quotes = [] }) {
  const [email, setEmail] = React.useState('');
  const [sku, setSku] = React.useState('');
  const [estado, setEstado] = React.useState('activo');
  const [rows, setRows] = React.useState(allAssignments());
  const [usuarios, setUsuarios] = React.useState([]);   // usuarios REGISTRADOS (base de datos)
  const [openLista, setOpenLista] = React.useState(false);
  const [filtroLista, setFiltroLista] = React.useState('');  // filtra la lista de asignaciones (escala a miles)
  const [pagina, setPagina] = React.useState(0);
  const [expandido, setExpandido] = React.useState('');      // email del usuario expandido
  const refrescar = () => setRows(allAssignments());
  const cargarUsuarios = React.useCallback(() => setUsuarios(listarUsuarios(undefined, quotes)), [quotes]);
  React.useEffect(() => {
    const hE = () => refrescar();
    const hU = () => cargarUsuarios();
    window.addEventListener('solarhub-entitlements', hE);
    window.addEventListener('solarhub-usuarios', hU);
    syncAllEntitlements();  // trae todas las asignaciones desde el backend (cross-device)
    syncUsuarios();         // trae los usuarios registrados desde la base (cross-device)
    cargarUsuarios();
    return () => { window.removeEventListener('solarhub-entitlements', hE); window.removeEventListener('solarhub-usuarios', hU); };
  }, [cargarUsuarios]);
  const asignar = () => {
    const e = email.trim();
    if (!e || !sku) return;
    setEntitlement(e, sku, estado);
    refrescar();
  };
  const inp = { background: C.dark, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 10px', color: C.text, fontSize: 12, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 10, color: C.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 };
  const pagBtn = { padding: '5px 11px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 11, fontWeight: 600, cursor: 'pointer' };
  const PAGE = 20;

  // Filtra los usuarios registrados por nombre/correo según lo tecleado (buscador desde la base).
  const q = email.trim().toLowerCase();
  const matches = usuarios
    .filter(u => !q || (u.email || '').toLowerCase().includes(q) || (u.nombre || '').toLowerCase().includes(q))
    .slice(0, 8);
  const yaAsignados = (em) => rows.filter(r => r.email === (em || '').toLowerCase()).length;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.heading, marginBottom: 2 }}>Administración de servicios (entitlements)</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
        Asigna qué servicio puede usar cada usuario para <strong style={{ color: C.heading }}>hacer pruebas</strong> mientras
        se habilita la compra. El usuario ve el servicio <strong>Activo</strong> al iniciar sesión con ese correo.
        (Al desplegar el <code>api/</code> real, el JWT con <code>servicios[]</code> toma el relevo.)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1.4fr) minmax(160px,1.4fr) minmax(140px,1fr) auto', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <label style={lbl}>Usuario registrado <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· busca por nombre o correo</span></label>
          <input value={email} onChange={e => { setEmail(e.target.value); setOpenLista(true); }}
            onFocus={() => setOpenLista(true)} onBlur={() => setTimeout(() => setOpenLista(false), 150)}
            placeholder={usuarios.length ? `Buscar entre ${usuarios.length} usuarios…` : 'usuario@correo.com'} style={{ ...inp, width: '100%' }} />
          {openLista && matches.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 3, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.18)', maxHeight: 260, overflowY: 'auto' }}>
              {matches.map(u => {
                const n = yaAsignados(u.email);
                const rolL = ROL_LABELS[u.rol] || u.rol;
                return (
                  <div key={u.id || u.email} onMouseDown={() => { setEmail((u.email || '').toLowerCase()); setOpenLista(false); }}
                    style={{ padding: '8px 11px', cursor: 'pointer', borderBottom: `1px solid ${C.border}33`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.nombre || u.email}</div>
                      <div style={{ fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 9, color: C.teal, border: `1px solid ${C.teal}44`, borderRadius: 4, padding: '1px 5px' }}>{rolL}</span>
                      {n > 0 && <span style={{ fontSize: 9, color: '#00c781' }}>{n} serv.</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label style={lbl}>Servicio</label>
          <select value={sku} onChange={e => setSku(e.target.value)} style={{ ...inp, width: '100%', cursor: 'pointer' }}>
            <option value="">Elige un servicio…</option>
            {(
              (() => {
                const dest = usuarios.find((u) => (u.email || '').toLowerCase() === email.trim().toLowerCase());
                const aud = dest ? audienciaDeRolPlataforma(dest.rol) : null;
                const lista = aud && aud !== 'admin' ? serviciosParaAudiencia(aud) : SERVICE_CATALOG;
                return lista;
              })()
            ).map(s => <option key={s.sku} value={s.sku}>{s.nombre} ({s.suite === 'fluxai' ? 'FluxAI' : 'Solar Hub'})</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Estado</label>
          <select value={estado} onChange={e => setEstado(e.target.value)} style={{ ...inp, width: '100%', cursor: 'pointer' }}>
            {ESTADOS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
 <button onClick={asignar} disabled={!email.trim() || !sku} style={{ padding: '9px 18px', background: (!email.trim() || !sku) ? `${C.muted}33` : C.teal, color: (!email.trim() || !sku) ? C.muted : C.heading, border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: (!email.trim() || !sku) ? 'default' : 'pointer' }}>✓ Asignar</button>
      </div>

      {rows.length > 0 && (() => {
        // ESCALA A MILES: agrupar por usuario (no una fila por asignación) + buscar + paginar +
        // expandir on-demand. Solo se renderiza la página visible → la vista no se cae con miles.
        const porUsuario = new Map();
        for (const r of rows) { if (!porUsuario.has(r.email)) porUsuario.set(r.email, []); porUsuario.get(r.email).push(r); }
        const q = filtroLista.trim().toLowerCase();
        const lista = [...porUsuario.entries()].filter(([e]) => !q || e.includes(q)).sort((a, b) => a[0].localeCompare(b[0]));
        const totalPaginas = Math.max(1, Math.ceil(lista.length / PAGE));
        const pag = Math.min(pagina, totalPaginas - 1);
        const visibles = lista.slice(pag * PAGE, pag * PAGE + PAGE);
        const rolDe = (em) => { const u = usuarios.find(x => (x.email || '').toLowerCase() === em); return u ? (ROL_LABELS[u.rol] || u.rol) : null; };
        return (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Asignaciones · {porUsuario.size} usuario{porUsuario.size === 1 ? '' : 's'} · {rows.length} servicio{rows.length === 1 ? '' : 's'}</div>
              <input value={filtroLista} onChange={e => { setFiltroLista(e.target.value); setPagina(0); }} placeholder="Filtrar por correo…" style={{ ...inp, width: 220, maxWidth: '100%' }} />
            </div>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
              {visibles.length === 0 && <div style={{ padding: 14, textAlign: 'center', color: C.muted, fontSize: 12 }}>Sin coincidencias.</div>}
              {visibles.map(([em, servs]) => {
                const abierto = expandido === em;
                const rol = rolDe(em);
                return (
                  <div key={em} style={{ borderBottom: `1px solid ${C.border}55` }}>
                    <button onClick={() => setExpandido(abierto ? '' : em)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: abierto ? `${C.teal}0d` : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                      <span style={{ color: C.muted, fontSize: 11, width: 12, flexShrink: 0 }}>{abierto ? '▾' : '▸'}</span>
                      <span style={{ fontWeight: 700, fontSize: 12, color: C.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{em}</span>
                      {rol && <span style={{ fontSize: 9, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>{rol}</span>}
                      <span style={{ marginLeft: 'auto', fontSize: 10.5, color: C.teal, fontWeight: 700, flexShrink: 0 }}>{servs.length} servicio{servs.length === 1 ? '' : 's'}</span>
                    </button>
                    {abierto && (
                      <div style={{ padding: '2px 11px 10px 31px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {servs.slice().sort((a, b) => (servicioPorSku(a.sku)?.nombre || a.sku).localeCompare(servicioPorSku(b.sku)?.nombre || b.sku)).map(r => {
                          const svc = servicioPorSku(r.sku);
                          const estLabel = (ESTADOS.find(e => e.id === r.estado) || {}).label || r.estado;
                          const habilita = r.estado !== 'inactivo';
                          const suite = svc?.suite === 'fluxai' ? 'FluxAI' : 'Solar Hub';
                          return (
                            <div key={r.sku} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5 }}>
                              <span style={{ color: C.heading, fontWeight: 600, minWidth: 150 }}>{svc?.nombre || r.sku}</span>
                              <span style={{ fontSize: 9.5, color: C.muted }}>{suite}</span>
                              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: habilita ? C.green : C.muted, border: `1px solid ${habilita ? C.green : C.muted}55`, borderRadius: 5, padding: '2px 7px' }}>{estLabel}</span>
                              <button onClick={() => removeEntitlement(r.email, r.sku)} style={{ marginLeft: 'auto', background: 'transparent', color: C.red, border: `1px solid ${C.red}44`, borderRadius: 5, padding: '3px 9px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Quitar</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {totalPaginas > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
                <button onClick={() => setPagina(Math.max(0, pag - 1))} disabled={pag === 0} style={{ ...pagBtn, opacity: pag === 0 ? 0.4 : 1 }}>← Anterior</button>
                <span style={{ fontSize: 11, color: C.muted }}>Página {pag + 1} de {totalPaginas}</span>
                <button onClick={() => setPagina(Math.min(totalPaginas - 1, pag + 1))} disabled={pag >= totalPaginas - 1} style={{ ...pagBtn, opacity: pag >= totalPaginas - 1 ? 0.4 : 1 }}>Siguiente →</button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default function Servicios({ go, openFlux, quotes = [], ordenes = [] }) {
  const [remoto, setRemoto] = React.useState(null);
  React.useEffect(() => {
    hubGet('servicios/catalogo').then((r) => {
      if (Array.isArray(r?.servicios) && r.servicios.length) setRemoto(r.servicios);
    }).catch(() => {});
  }, []);
  const claims = currentClaims();
  const aud = audience(claims);
  const esAdmin = aud === 'admin';
  const premiumOk = puedePremium(claims);
  const conTelemetria = tieneMonitoreoFluxAI(claims);

  // El ADMIN ve TODO el catálogo. El resto ve lo de su audiencia (invitado = los abiertos a todos).
  const fuente = remoto && remoto.length ? remoto : SERVICE_CATALOG;
  const visibles = esAdmin
    ? fuente
    : fuente.filter(s => (s.audiencia || []).includes(aud) || (aud == null && (s.audiencia || []).includes(null)));

  // Abrir un servicio: si tiene vista en Solar Hub → navega. Si es de FluxAI (nivel máquina) →
  // deep-link a la consola de FluxAI con la MISMA sesión (SSO si hay token firmado).
  const abrir = (s) => {
    // El SERVICIO PRINCIPAL "Estudio fotovoltaico" es NATIVO (`ruta: 'estudio'`), no embebido:
    // el iframe exigía que el token de la suite reanudara sesión en FluxAI y hoy va sin firmar,
    // así que casi todo usuario aterrizaba en un login ajeno. Se quitó el atajo `abreFlux` para
    // que nadie lo vuelva a cablear sin querer; los servicios de FluxAI (nivel máquina) siguen
    // embebiéndose más abajo por `suite === 'fluxai'`.
    if (s.ruta && go) { go(s.ruta); return; }
    if (s.alex) {
      try { window.dispatchEvent(new Event('alex:open')); } catch { /* */ }
      return;
    }
    if (s.suite === 'fluxai') {
      // Dato de FluxAI, UI de FluxAI: pestaña a ESA ruta. No se embebe el dashboard.
      try { window.open(fluxaiServiceUrl(s.sku), '_blank', 'noopener,noreferrer'); } catch { /* popup bloqueado */ }
    }
  };

  const Tarjeta = (s) => {
    // Prerrequisito de telemetría FluxAI (informe ISO COMPLETO): si lo exige y el usuario no
    // tiene equipos en telemetría → versión completa bloqueada (queda la estimada). El admin no se gatea.
    const requiereTelemetria = s.requiere === 'monitoreo_premium';
    const faltaTelemetria = !esAdmin && requiereTelemetria && !conTelemetria;
    const contratado = s.requiereContrato ? tieneServicio(s.sku, claims) : true;
    // El servicio está ACCESIBLE si lo tiene CONTRATADO por SKU (entitlement asignado o JWT) O
    // por acceso premium de rol (técnico/operador/admin). Así, asignar el servicio al usuario
    // (panel admin) NO solo prende el badge "Activo": también ABRE el botón. (Antes el botón
    // miraba sólo `premiumOk` → quedaba "Requiere plan" pese a estar asignado.)
    const estudioPorCuota = s.sku === 'solar_study' && isTecnico(claims)
      && tecnicoGanoEstudioGratis(claims, { quotes, ordenes });
    const bloqueado = s.sku === 'solar_study'
      ? false
      : !esAdmin && (faltaTelemetria || (s.tier === 'premium' && !contratado && !premiumOk && !estudioPorCuota)
      || (s.sku === 'solar_study' && isTecnico(claims) && !estudioPorCuota && !contratado));
    const esFlux = s.suite === 'fluxai';
    const proximo = s.disponible === false;
    const abrible = !proximo && !bloqueado && (s.ruta || esFlux || s.alex);
    const acc = esFlux ? FLUX : C.yellow;   // FluxAI azul cuando se cita FluxAI · Solar Hub naranja

    // ── Estado de entitlement (la LÓGICA, visible para TODOS incl. admin) ──
    // gratis → Abierto · premium contratado → Activo · premium sin plan → Requiere plan.
    let estado, estadoCol;
    if (s.sku === 'solar_study' || s.tier !== 'premium') { estado = 'Abierto'; estadoCol = '#00c781'; }
    else if (contratado || estudioPorCuota) { estado = estudioPorCuota && !contratado ? 'Gratis este mes' : 'Activo'; estadoCol = '#00c781'; }
    else if (faltaTelemetria) { estado = 'Requiere monitoreo'; estadoCol = C.amber; }
    else { estado = 'Requiere plan'; estadoCol = C.amber; }

    const Badge = ({ txt, col }) => (
      <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        color: col, background: `${col}18`, border: `1px solid ${col}44`, borderRadius: 5, padding: '3px 7px' }}>{txt}</span>
    );

    return (
      <div key={s.sku} style={{
        background: C.card, border: `1px solid ${C.border || '#1b2c3a'}`, borderLeft: `3px solid ${acc}`,
        borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 7, position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {/* Ícono: servicio de FluxAI → su LOGO ORIGINAL (rayo verde→azul); Solar Hub → su ícono (naranja). */}
          <div style={{
            width: 32, height: 32, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${acc}14`, border: `1px solid ${acc}33`, color: acc, flexShrink: 0,
          }}>{esFlux ? <FluxBolt size={17} /> : <ServiceIcon sku={s.sku} size={17} />}</div>
          {/* UN solo badge con color (el ESTADO); el resto neutro → sin exceso de color. */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {esFlux && <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: FLUX, display: 'inline-flex', alignItems: 'center', gap: 3 }}><FluxBolt size={11} />FluxAI</span>}
            <Badge txt={s.tier === 'premium' ? 'Premium' : 'Gratis'} col={C.muted} />
            <Badge txt={estado} col={estadoCol} />
          </div>
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: C.heading, marginTop: 2 }}>{s.nombre}</div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, flex: 1 }}>{s.desc}</div>
        {/* Botón de acción SOBRIO (link de consola), no bloque sólido grande: mismo lenguaje para
            ambas marcas (acento de la marca como borde/texto, no relleno saturado). */}
        <button
          onClick={() => abrir(s)}
          disabled={!abrible}
          title={faltaTelemetria ? 'La versión COMPLETA (medido vs simulado) requiere equipos con monitoreo activo.' : bloqueado ? 'Requiere cuenta técnica o servicio contratado' : undefined}
          style={{
            marginTop: 4, alignSelf: 'flex-start', borderRadius: 6, padding: '6px 12px', fontWeight: 700, fontSize: 11.5,
            cursor: abrible ? 'pointer' : 'default', textAlign: 'center',
            background: !abrible ? `${C.muted}14` : `${acc}16`,
            color: !abrible ? C.muted : acc,
            border: `1px solid ${!abrible ? C.muted + '33' : acc + '55'}`,
          }}
        >
          {proximo ? 'Pronto' : bloqueado ? (s.sku === 'solar_study' && isTecnico(claims) ? 'Cierra 1 proyecto este mes' : faltaTelemetria ? 'Requiere monitoreo' : 'Requiere plan') : s.principal ? 'Abrir estudio →' : s.ruta ? 'Abrir →' : esFlux ? 'Abrir en FluxAI →' : 'Incluido en tu plan'}
        </button>
        {esAdmin && s.requiereContrato && (
          <div style={{ fontSize: 10, color: C.teal, marginTop: 2, lineHeight: 1.3 }}>
            Servicio de pago — el cliente lo ve <strong>Activo</strong> solo si está en sus <code>servicios[]</code>. Como admin lo abres sin gatear.
          </div>
        )}
        {faltaTelemetria && (
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4, lineHeight: 1.3 }}>
            Versión estimada disponible desde la cotización; la completa (M&V) se habilita con equipos con monitoreo activo.
          </div>
        )}
      </div>
    );
  };

  const SectionHead = ({ titulo, sub, accent }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '24px 0 12px', flexWrap: 'wrap' }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: C.heading, letterSpacing: '-0.3px' }}>{titulo}</h2>
      <span style={{ fontSize: 11, color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>{sub}</span>
    </div>
  );

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 };

  return (
    <div className="al-content" style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 18px 40px', width: '100%' }}>
      <div style={{ marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: C.heading, letterSpacing: '-0.4px' }}>Servicios</h1>
        <p style={{ marginTop: 6, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          La <strong style={{ color: C.heading }}>cotización rápida es gratis</strong>.
          El estudio de detalle y la telemetría se consumen solo si están contratados.
          {claims && <> Sesión: <strong style={{ color: C.heading }}>{profileLabel(claims)}</strong>.</>}
        </p>
      </div>

      {esAdmin && <EntitlementsAdmin quotes={quotes} />}

      {(aud === 'user' || aud == null) ? (
        <>
          <SectionHead titulo="Tus servicios activos" sub="Incluidos con la plataforma" accent="#00c781" />
          <div style={grid}>
            {visibles.filter((s) => s.tier === 'gratis' && s.disponible !== false && s.sku !== 'agua_gas').map(Tarjeta)}
          </div>
          <SectionHead titulo="Amplía tu plan" sub="Como en FluxAI · cada marca con su color" accent={C.yellow} />
          <p style={{ margin: '-4px 0 12px', fontSize: 12, color: C.muted }}>
            Los de Solar Hub se abren aquí (naranja). Los de FluxAI se abren en FluxAI (azul).
          </p>
          <div style={grid}>
            {visibles.filter((s) => s.tier === 'premium' || s.sku === 'agua_gas').map(Tarjeta)}
          </div>
        </>
      ) : (
        serviciosPorCapa(visibles).map((g) => (
          <div key={g.capa}>
            <SectionHead titulo={g.titulo} sub={g.sub} accent={g.capa === 'ami' ? C.teal : C.yellow} />
            <div style={grid}>{g.items.map(Tarjeta)}</div>
          </div>
        ))
      )}

      <div style={{ marginTop: 22, fontSize: 11, color: C.muted, lineHeight: 1.5, background: `${C.teal}0d`, border: `1px solid ${C.teal}22`, borderRadius: 10, padding: '12px 14px' }}>
        Cotizador gratis en Solar Hub. Estudio, ISO y SCADA se consumen aparte.
        El técnico de una instalación se asigna en Administración cuando la propuesta está ganada
        (matching RETIE → orden). El técnico de cuenta (quién atiende al usuario) es otro plano.
      </div>
    </div>
  );
}
